import React from 'react';
import _, { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { ServiceManager } from '@jutro/legacy/services';
import { withValidation, validationPropTypes } from '@xengage/gw-portals-validation-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { AddressLookupService } from 'gw-capability-address';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { MLGeoCompleteService } from 'ml-services-common-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import metadata from './MLAddressLookupComponent.metadata.json5';
import styles from './MLAddressLookupComponent.module.scss';
import messages from './MLAddressLookupComponent.messages';
import appConfig from 'app-config';
import { FieldComponent } from '@jutro/legacy/components';
import { TranslatorContext } from '@jutro/legacy/locale';

function buildFullAddress(address) {
    const forAddressLine2 = (address.addressLine2) ? `, ${address.addressLine2}` : '';
    return `${address.addressLine1}${forAddressLine2}, ${address.city}, ${address.state}, ${address.postalCode}`;
}

class MLAddressLookupComponent extends FieldComponent {
    static contextType = TranslatorContext;

    /**
     * @memberof gw-capability-address-react.AddressLookupComponent
     * @prop {Object} propTypes - the props that are passed to this component
     * @prop {string} propTypes.postalCode - the postal code entered in the landing page
     * @prop {function} propTypes.onValueChange - callback when change is made
     * @prop {string} propTypes.path - path to value in the view modal
     * @prop {function} propTypes.onValidate - returns if the values are valid
     */
    static propTypes = {
        value: PropTypes.shape({ postalCode: PropTypes.string }),
        path: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        onValidate: PropTypes.func,
        onValueChange: PropTypes.func,
        label: PropTypes.string,
        labelPosition: PropTypes.string,
        required: PropTypes.bool,
        sameMailingAddress: PropTypes.bool,
        searchLabel: PropTypes.shape({}),
        addressLine1Placeholder: PropTypes.string,
        searchAddress: PropTypes.bool,
        zipCodePlaceholder: PropTypes.string,
        initialLockStateCity: PropTypes.bool,
        showStateCode: PropTypes.bool,
        showValidationErrors: PropTypes.bool,
        sectionLabel: PropTypes.shape({}),
        cityStateLookUp: PropTypes.bool,
        isMailingAddress: PropTypes.bool,
        tooltipTitle: PropTypes.string,
        tooltipText: PropTypes.string,
        isModernProduct: PropTypes.bool,
        ...FieldComponent.propTypes,
        ...validationPropTypes
    };

    static defaultProps = {
        onValidate: undefined,
        value: undefined,
        onValueChange: undefined,
        layout: 'full-width',
        hideLabel: true,
        label: undefined,
        labelPosition: undefined,
        required: false,
        addressLine1Placeholder: undefined,
        searchAddress: true,
        showValidationErrors: false,
        initialLockStateCity: true,
        isMailingAddress: false,
        isModernProduct: true
    };

    state = {
        formData: {},
        showManualAddress: false,
        addressResultList: [],
        lockState: false,
        lockCity: false,
        newSearch: true,
        manualAddressEntry: false,
        searchingResult: false,
        showPropertyView: true,
        showResultList: true,
        isAddressPopulated: false,
        showEditAddress: false,
        hasMoreThan5AddrResults: false,
        activeItem: 0,
        isQuoteAndBuy: false,
        editAddressAnnouncement: ''
    };

    handleClick = (e) => {
        const { showResultList } = this.state;
        const addressLookupSearch = document.querySelector('input[id^="addresslookupSearch"]');
        if ((addressLookupSearch && addressLookupSearch.contains(e.target) && showResultList)) {
            this.setState({ showResultList: true });
            return true;
        }
        this.setState({ showResultList: false });
        return false;
    }

    QUOTED_SUBMISSIONS_KEY = 'quotedSubmissionNumbers';
    isQuoted = (quoteNumber) => {
        if (!quoteNumber) return false;
        const list = JSON.parse(localStorage.getItem(this.QUOTED_SUBMISSIONS_KEY) || '[]');
        return list.includes(quoteNumber);
    };

    componentDidMount() {
        const {
            id, onValidate, isComponentValid, registerComponentValidation, value: addressData, initialLockStateCity, submissionVM
        } = this.props;
        const { showPropertyView } = this.state;

        const portalCode = appConfig.portalCode;

        this.setState({
            portalCode,
            isQuoteAndBuy: portalCode === 'QB'
        })

        registerComponentValidation(this.isFieldsValid);

        if (onValidate) {
            onValidate(isComponentValid, id);
        }

        if (this.isFieldsValid()) {
            this.setState({ manualAddressOverride: true });
        }

        const localeService = ServiceManager.getService('locale-service');
        const defaultCountryCode = localeService.getDefaultCountryCode();
        if (addressData.value) {
            addressData.country.value = addressData.country.value || defaultCountryCode;
        } else {
            addressData.value = {
                country: defaultCountryCode
            };
        }

        if (addressData.addressLine1 && addressData.city
            && addressData.addressLine1.value && addressData.city.value
            && showPropertyView) {
            this.setState({
                formData: {
                    searchText: buildFullAddress(addressData.value),
                },
                isAddressPopulated: true
            });
        }

        this.setState({ lockState: initialLockStateCity, lockCity: initialLockStateCity });
        window.addEventListener('click', this.handleClick);
    }

    componentDidUpdate(prevProps) {
        const {
            id,
            onValidate,
            isComponentValid,
            hasValidationChanged,
            value: newAddressData
        } = this.props;
        const { value: oldAddressData, } = prevProps;
        const isSameAddress = _.isEqual(newAddressData.value, oldAddressData.value);

        if ((!isSameAddress || hasValidationChanged) && onValidate) {
            onValidate(isComponentValid, id);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('click', this.handleClick);
    }

    isFieldsValid = () => {
        const { value: address } = this.props;
        const addressVmAspects = _.get(address, 'address.aspect', {});
        if (!_.isEmpty(addressVmAspects)
            && (!addressVmAspects.valid || !addressVmAspects.subtreeValid)) {
            return false;
        }
        return true;
    };

    handleChange = (value, changedPath) => {
        const { path, onValueChange } = this.props;
        const { formData } = this.state;

        _.set(formData, changedPath, value);

        onValueChange(value, `${path}.${changedPath}`);

        this.setState({ formData });
    };

    handleChangeModernQnB = (value, changedPath) => {
        const { onValueChange, path } = this.props;

        this.setState((prevState) => {
            const updatedFormData = _.cloneDeep(prevState.formData || {});

            _.set(updatedFormData, changedPath, value);

            onValueChange(value, `${path}.${changedPath}`);

            return { formData: updatedFormData };
        });
    };

    populateSelectedAddress = (selectedItem) => {
        const translator = this.context;
        const { value: addressData, onValueChange, path } = this.props;
        const clonedAddressData = _.clone(addressData.value);
        
        if (selectedItem.formattedAddress === `${translator(messages.addressNotInListMessage)}`) {
            clonedAddressData.addressLine1 = undefined;
            clonedAddressData.addressLine2 = undefined;
            this.setState({
                lockCity: false,
                lockState: false,
                manualAddressEntry: true,
                isAddressPopulated: false
            });
        } else {
            clonedAddressData.addressLine1 = selectedItem.addressData.AddressLine1;
            clonedAddressData.addressLine2 = selectedItem.addressData.AddressLine2;
            clonedAddressData.city = selectedItem.addressData.City;
            clonedAddressData.state = selectedItem.addressData.StateProvince;
            clonedAddressData.postalCode = selectedItem.addressData.PostalCode;
            clonedAddressData.standardizeOverride_ML = false;
            clonedAddressData.standardizeDate_ML = MLLocalDateUtil.date2LocalDate(new Date());
            clonedAddressData.validateAddressStatus_ML = true;

            const selectedAddressDisplayName = buildFullAddress(clonedAddressData);
            clonedAddressData.displayName = selectedAddressDisplayName;
            this.setState({
                manualAddressEntry: false,
                isAddressPopulated: true,
                showEditAddress: true,
                formData: {
                    searchText: selectedAddressDisplayName,
                }
            });
        }
        this.setState({
            newSearch: false,
            showManualAddress: true,
            searchingResult: false,
            showPropertyView: false,
            showResultList: false
        });
        onValueChange(clonedAddressData, path);
    }

    writeValue = (value, path) => {
        const { formData } = this.state;
        const clonedFormData = _.cloneDeep(formData);
        _.set(clonedFormData, path, value);

        this.setState({
            formData: clonedFormData,
        });
    };

    selectItemOnHover = (index) => {
        this.setState({
            activeItem: index
        });
    };

    generateAddressOverrides = () => {
        const { addressResultList, activeItem } = this.state;
        const addressListContentOverrides = addressResultList.map((item, index) => {
            const isSelected = index === activeItem;
            return {
                [`addressValue${index}`]: {
                    role: "button",
                    "aria-selected": isSelected,
                    onClick: () => this.populateSelectedAddress(item),
                    onMouseEnter: () => this.selectItemOnHover(index),
                    onKeyDown: this.handleKeyDown,
                    content: item.formattedAddress,
                    className: isSelected ? 'active list-color' : 'list-color',
                    tabindex: "0",
                    "aria-hidden": "true"
                }
            };
        });

        return Object.assign({}, ...addressListContentOverrides);
    };

    searchAddress = (value, path) => {
        this.writeValue(value, path);

        if (value && value.length >= 4) {
            this.setState({ showPropertyView: false, showResultList: true });
            this.retrieveAddress(value);
        } else if (_.isEmpty(value)) {
            // To Reset the address when user manually remove the search address
            this.resetAddress()
        }
    }

    retrieveAddress = debounce(async (address) => {
        const { value: addressData, sameMailingAddress } = this.props;
        const addresses = await MLGeoCompleteService.retrieveAddress(address, addressData, sameMailingAddress === false)
        const translator = this.context;

        const moreThan5Addresses = addresses.length > 5;
        const limit5Addresses = moreThan5Addresses ? addresses.slice(0, 5) : addresses;
        if (!moreThan5Addresses) {
            limit5Addresses.push({ formattedAddress: `${translator(messages.addressNotInListMessage)}` });
        }

        this.setState({ addressResultList: limit5Addresses, searchingResult: true, hasMoreThan5AddrResults: moreThan5Addresses, activeItem: 0 });
    }, 300);

    onSearchPostalCode = (value, path) => {
        const { cityStateLookUp, isModernProduct } = this.props;
        const { isQuoteAndBuy } = this.state;
        const handleFieldChange = isQuoteAndBuy && isModernProduct && window.location.pathname.includes('/drivers') ? this.handleChangeModernQnB : this.handleChange;
        handleFieldChange(value, path);
        
        if (cityStateLookUp) {
            if (/^\d{5}(-\d{4})?$/.test(value)) {
                this.searchPostalCode(value);
            }
        }
    }

    searchPostalCode = (postalCode) => {
        MLGeoCompleteService.getStates(postalCode.substring(0, 5))
            .then((states) => {
                this.statesCallback({ states });
            })
            .catch((error) => {
                console.error(error);
                this.statesCallback({ postalCode });
            });
    };

    statesCallback = async ({ states: data, postalCode }) => {
        const { value: addressData, onValueChange, path } = this.props;
        const newAddressData = _.clone(addressData.value);
        if (data && data.length === 1 && data[0].City && data[0].StateProvince) {
            newAddressData.city = data[0].City;
            newAddressData.state = data[0].StateProvince;
        } else {
            const lookUpParams = {
                postalCode: postalCode.substring(0, 5)
            };
            const lookUpPromise = await AddressLookupService.lookUpCityAndState(lookUpParams);
            newAddressData.city = lookUpPromise.city ? lookUpPromise.city : undefined;
            newAddressData.state = lookUpPromise.state ? lookUpPromise.state : undefined;
        }
        this.setState({
            lockCity: newAddressData.city !== undefined,
            lockState: newAddressData.state !== undefined
        });
        onValueChange(newAddressData, path);
    }

    resetAddress = () => {
        const { path, onValueChange } = this.props;

        this.setState({
            isAddressPopulated: false,
            showEditAddress: false,
            formData: {
                searchText: ''
            }
        });

        onValueChange({ country: 'US' }, path)
    };

    errorMessage = () => {
        const { isAddressPopulated } = this.state;
        const { value: address, showValidationErrors } = this.props;
        if ((showValidationErrors || isAddressPopulated) && !address.addressLine1?.aspects.valid) {
            return address.addressLine1?.aspects.validationMessage;
        }
        if ((showValidationErrors || isAddressPopulated) && !address.city?.aspects.valid) {
            return address.city?.aspects.validationMessage;
        }
        return false;
    };

    showAddressForm = () => {
        const translator = this.context;
        
        this.setState({
            formData: {
                searchText: ''
            },
            showPropertyView: false,
            manualAddressEntry: true,
            editAddressAnnouncement: translator(messages.addressAlertAnnouncement)
        });

        setTimeout(() => { 
            document.querySelector('input[id*="addressLine1_"]').focus()
        }, 100);
    };

    chooseActiveItem = () => {
        const { activeItem, addressResultList } = this.state;
        if (!_.isNil(activeItem) && !_.isEmpty(addressResultList)) {
            this.populateSelectedAddress(addressResultList[activeItem]);
        }
    };

    cancelSelection = () => {
        this.setState({
            activeItem: undefined,
            addressResultList: [],
            showResultList: false
        });
    };

    selectNextItem = () => {
        const { activeItem, addressResultList } = this.state;
        this.setState({
            activeItem: activeItem < _.size(addressResultList) - 1 ? activeItem + 1 : 0
        });
    };

    selectPreviousItem = () => {
        const { activeItem, addressResultList } = this.state;
        this.setState({
            activeItem: activeItem > 0 ? activeItem - 1 : _.size(addressResultList) - 1
        });
    };

    handleInputKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                this.selectPreviousItem();
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.selectNextItem();
                break;
            case 'Enter':
            case 'Tab':
                this.chooseActiveItem();
                break;
            case 'Escape':
                this.cancelSelection();
                break;
            default: // do nothing
        }
    };

    setInputEvents = (element) => {
        if (element) {
            const input = element.querySelector('input');
            input.addEventListener('keydown', this.handleInputKeyDown);
        }
    };

    getSelectedAddressTitle = () => {
        const { addressResultList, activeItem } = this.state;
        
        if (addressResultList.length > 0) {
            // eslint-disable-next-line security/detect-object-injection
            return `${addressResultList.length} suggestions available.  Selected Address is ${addressResultList[activeItem].formattedAddress}.  Continue typing or use the arrow keys to select a different address.  Press enter to select this address.`;
        }

        return '';

    };

    renderControl() {
        const {
            formData,
            addressResultList,
            lockState,
            lockCity,
            newSearch,
            manualAddressEntry,
            showPropertyView,
            showResultList,
            isAddressPopulated,
            showEditAddress,
            hasMoreThan5AddrResults,
            isQuoteAndBuy
        } = this.state;
        const {
            value: address,
            label, labelPosition,
            searchLabel,
            sameMailingAddress,
            addressLine1Placeholder,
            searchAddress,
            zipCodePlaceholder,
            showStateCode,
            showValidationErrors,
            sectionLabel,
            isMailingAddress,
            tooltipTitle,
            tooltipText,
            submissionVM,
            isModernProduct
        } = this.props;

        const isQBNP = isQuoteAndBuy && isModernProduct && (window.location.pathname.includes('/drivers') || window.location.pathname.includes('/vehicles'));

        const dataForComponent = isQBNP ? {
            ...formData,
            ...address
        } : {
            ...formData,
            ...address
        };

        const translator = this.context;
        const searchAddressPlaceholder = newSearch ? searchLabel : `${translator(messages.searchAddressAgain)}`;
        const constructCityStateCode = () => {
            const city = _.get(address, 'city.value', '');
            const stateCode = _.get(address, 'state.value.code', '');
            return `${city}, ${stateCode}`;
        };

        const overrideProps = {
            '@field': {
                showOptional: true,
                labelPosition
            },
            addresslookupSearch: { // required message is displayed in errorMsgContainer
                onValueChange: this.searchAddress,
                label: sectionLabel,
                placeholder: searchAddressPlaceholder,
                showOptional: false,
                tooltip: {
                    title: tooltipTitle,
                    text: tooltipText,
                    placement: 'bottom'
                },
                onKeyDown: this.handleInputKeyDown,
                controlClassName: this.errorMessage() ? "errorInput" : "",
                disabled: isQuoteAndBuy && (_.get(submissionVM, 'baseData.status_ML.value') === 'Quoted' || this.isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))),
                className: isQuoteAndBuy ? "addressLookupFieldQb" : "addressLookupField"
            },
            errorMsgContainer: {
                visible: !manualAddressEntry && this.errorMessage(),
                className: `${isQuoteAndBuy ? 'form-error-content-message errorMsgContainer qnbErrorMsgContainer' : 'form-error-content-message errorMsgContainer'}`
            },
            errorMessage: {
                content: this.errorMessage(),
                "aria-description": `Insured Address Error: ${this.errorMessage()}`
            },
            searchContainer: {
                visible: !manualAddressEntry
            },
            addressManualContainer: {
                visible: !searchAddress || manualAddressEntry
            },
            addressListContent: {
                visible: !showPropertyView && showResultList,
                "aria-description": "Search results displayed",
                role: "alert"
            },
            iterableAddress: {
                data: addressResultList,
                onKeyDown: this.handleKeyDown,
                role: "listbox"
            },
            postalCode: {
                onValueChange: this.onSearchPostalCode,
                placeholder: zipCodePlaceholder || platformMessages.zipCode
            },
            editStateContainer: {
                visible: !lockState,
                className: isQuoteAndBuy ? "col-md-3 vertical-spacing-15" : "col-md-3"
            },
            editCityContainer: {
                visible: !lockCity,
                className: isQuoteAndBuy ? "col-md-4 vertical-spacing-15" : "col-md-4"
            },
            readonlyCity: {
                content: (
                    <>
                        {address.city && `${address.city.value},`}
                        &nbsp;
                    </>
                ),
                className: !sameMailingAddress && styles.mailingReadOnly
            },
            readonlyCityStateNameContainer: {
                visible: !showStateCode && lockCity && lockState
            },
            readonlyCityStateCodeContainer: {
                visible: (showStateCode === true) && lockCity && lockState
            },
            readonlyCityState: {
                content: constructCityStateCode(),
                className: !sameMailingAddress && styles.mailingReadOnly
            },
            manualAddressLabel: {
                visible: searchAddress && manualAddressEntry,
                content: translator(sectionLabel)
            },
            addressLine1: {
                placeholder: addressLine1Placeholder || translator(messages.addressLine1Placeholder)
            },
            addressLookupContainer: {
                visible: searchAddress
            },
            locationIcon: {
                visible: !isAddressPopulated
            },
            greenIconPopulatedIcon: {
                visible: isAddressPopulated,
                className: isQuoteAndBuy ? "icon-complete-QnB icon-ml-status-check-complete address-selected" : "icon-ml-status-check-complete icon-green address-selected"
            },
            resetAddressIcon: {
                visible: isAddressPopulated && !(isQuoteAndBuy && (_.get(submissionVM, 'baseData.status_ML.value') === 'Quoted' || this.isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value')))),
                onClick: this.resetAddress,
            },
            searchAddressLine2: {
                showOptional: false,
                disabled: isQuoteAndBuy && (_.get(submissionVM, 'baseData.status_ML.value') === 'Quoted' || this.isQuoted(submissionVM?.quoteID?.value) || !_.isEmpty(_.get(submissionVM, 'quoteData.paymentQuotes_ML.value'))),
                label: isQuoteAndBuy ? messages.addressLine2Placeholder : " ",
                className: isQuoteAndBuy ? "" : "no-label-top-spacing"
            },
            editAddress: {
                visible: showEditAddress,
                onClick: this.showAddressForm,
                className: `${isQuoteAndBuy ? 'marginTopQB' : ''}`,
                'role': isQuoteAndBuy ? 'link' : 'button'
            },
            editAddressLiveRegion: {
                content: isQuoteAndBuy ? this.state.editAddressAnnouncement : ""
            },
            mailingAddress: {
                visible: !showEditAddress && !!isMailingAddress,
                onClick: this.showAddressForm,
                className: `${isQuoteAndBuy ? 'qnbMailingLink' : ''}`
            },
            addressSearchContainer: {
                ref: this.setInputEvents,
                // eslint-disable-next-line no-nested-ternary
                className: isQBNP ? 'vertical-spacing-15 col-md-4 location-type-ahead col-md-4-qb qnbModern-half-width' : isQuoteAndBuy ? 'vertical-spacing-15 col-md-4 location-type-ahead col-md-4-qb' : 'vertical-spacing-15 col-md-4 location-type-ahead'
            },
            searchAddressLine2Container:{
                className: isQuoteAndBuy ? 'vertical-spacing-15 col-md-4' : 'col-md-2'
            },
            continueTypingForResultsContainer: {
                visible: hasMoreThan5AddrResults
            },
            addressListLiveRegion: {
                content: this.getSelectedAddressTitle()
            },
            ...this.generateAddressOverrides()
        };
        const resolvers = {
            resolveClassNameMap: styles
        };

        return (
            <ViewModelForm
                uiProps={metadata.componentContent}
                model={dataForComponent}
                overrideProps={overrideProps}
                onValueChange={isQBNP ? this.handleChangeModernQnB : this.handleChange}
                classNameMap={resolvers.resolveClassNameMap}
                showErrors={showValidationErrors}
            />
        );
    }

    render() {
        return super.render();
    }
}

export default withAuthenticationContext(withValidation(MLAddressLookupComponent));